import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MaintenanceConfig } from '../model/maintenance-config';
import { interval, Observable, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith, tap } from 'rxjs/operators';
import { MaintenanceState } from '../enum/maintenance-state.enum';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceService {
  
  private readonly http = inject(HttpClient);
  private readonly destroyRef$ = inject(DestroyRef);
  
  private readonly _isActive = signal(false);
  private readonly _emailSupport = signal('');
  private readonly _estimatedTimeInMs = signal(0);
  private readonly _startAt = signal<Date | null>(undefined);
  
  readonly maintenance = {
    isActive: this._isActive.asReadonly(),
    emailSupport: this._emailSupport.asReadonly(),
    estimatedTimeInMs: this._estimatedTimeInMs.asReadonly(),
    startAt: this._startAt.asReadonly()
  };
  
  readonly maintenanceState = computed(() => {
    const isActive = this._isActive();
    const startAt = this._startAt();
    const duration = this._estimatedTimeInMs();
    const now = Date.now();
    
    if (!isActive || !startAt || !duration) {
      return MaintenanceState.OFF;
    }
    
    const start = startAt.getTime();
    const end = start + duration;
    
    if (now < start) {
      return MaintenanceState.SCHEDULED;
    }
    
    if (now > end) {
      return MaintenanceState.OFF;
    }
    
    return MaintenanceState.ACTIVE;
  });
  
  constructor() {
    this._checkMaintenanceConfig();
  }
  
  private _checkMaintenanceConfig(): void {
    interval(environment.maintenance.maintenanceWatcherInMs).pipe(
      startWith(0),
      takeUntilDestroyed(this.destroyRef$),
      switchMap(() => this._loadConfig$())
    ).subscribe();
  }
  
  private _loadConfig$(): Observable<MaintenanceConfig> {
    // Petite astuce pour contourner le cache gcp sur le fichier.json via l'ajout d'un queryParams changeant
    return this.http.get<MaintenanceConfig>(`${environment.maintenance.maintenanceConfigPath}?t=${Date.now()}`).pipe(
      tap(config => {
        this._isActive.set(config.isActive);
        this._emailSupport.set(config.emailSupport);
        this._estimatedTimeInMs.set(config.estimatedTime);
        this._startAt.set(config.startAt ? new Date(config.startAt) : undefined);
      })
    );
  }
  
  public getLocalStartTime(locale: string): Date {
    return new Date(
      this._startAt().getTime()
    );
  }
  
  public getLocalEndTime(locale: string): Date {
    return new Date(
      this._startAt().getTime() + this._estimatedTimeInMs()
    );
  }
  
  public getMaintenanceProgressPercent(): number {
    const startAt = this._startAt();
    const totalMs = this._estimatedTimeInMs();
    
    if (!startAt || !totalMs || totalMs <= 0) {
      return 0;
    }
    
    const start = startAt.getTime();
    const now = Date.now();
    
    if (now < start) {
      return 0;
    }
    
    const elapsed = now - start;
    
    const progress = (elapsed / totalMs) * 100;
    
    return Math.floor(Math.max(0, Math.min(100, progress)));
  }
}
